import '@/styles/style.css';

import ui from '@nuxt/ui/vue-plugin';
import { createHead } from '@unhead/vue/client';
import { createPinia } from 'pinia';
import type { Plugin } from 'vue';
import { createApp } from 'vue';

import { setupValibotI18n } from '@teamango/core';

import App from '@/App.vue';
import { apolloClient } from '@/core/apollo/client';
import { setupLaunchQueue } from '@/core/pwa/launch-queue';
import router from '@/core/router';
import { useTheme } from '@/shared/composables/useTheme';

setupValibotI18n();

// Initialize the color-mode singleton before mount so the resolved theme class
// is applied to <html> as early as possible (the inline script in index.html
// handles the very first paint).
useTheme();

import * as plugins from '@/core/plugins';
import * as ApolloComposable from '@vue/apollo-composable';

import { registerModules } from '@/core/register-modules.ts';

import { AuthModule } from '@/modules/auth';
import { EpicsModule } from './modules/epics';
import { ProgramsModule } from './modules/programs';
import { HelpModule } from './modules/help';
import { InboxModule } from './modules/inbox';
import { IssuesModule } from './modules/issues';
import { LabelsModule } from './modules/labels';
import { OauthModule } from './modules/oauth';
import { OrganizationsModule } from './modules/organizations';
import { ProjectsModule } from './modules/projects';
import { ReleasesModule } from './modules/releases';
import { SandboxModule } from './modules/sandbox';
import { SavedViewsModule } from './modules/saved-views';
import { TeamsModule } from './modules/teams';
import { UsersModule } from './modules/users';

registerModules({
  AuthModule,
  EpicsModule,
  ProgramsModule,
  HelpModule,
  InboxModule,
  IssuesModule,
  LabelsModule,
  OauthModule,
  OrganizationsModule,
  ProjectsModule,
  ReleasesModule,
  SandboxModule,
  SavedViewsModule,
  TeamsModule,
  UsersModule,
});

const app = createApp(App);

app.use(createPinia());
app.use(router);
// Created here rather than left to `@nuxt/ui/vue-plugin` (which only adds one
// when none is installed) so the head every `useHead` writes to is explicit.
app.use(createHead());
app.use(ui);
app.provide(ApolloComposable.ApolloClients, {
  default: apolloClient,
});

for (const plugin of Object.values(plugins) as Plugin[]) {
  app.use(plugin);
}

router.isReady().then(() => {
  app.mount('#app');
  setupLaunchQueue(router);
});
